import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plane, Ship, Globe, Users, Heart, Cake, Briefcase, Star,
  Calendar, MapPin, CheckCircle, ArrowRight, Phone, Mail,
  Anchor, Mountain, Palmtree, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand Assets
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/aa1f5b83-186d-4ac3-9dce-2dfcd3ce4c84/images/c740998ea2d13be287d1e30ff6aab30e0baab2ea64c21abe150888905a05c219.jpeg";
const BRANDI_PHOTO = "https://customer-assets-jt897jd0.emergentagent.net/job_tranquil-living-1/artifacts/sjqqjm8y_IMG_5382.webp";
const HERO_IMAGE = "https://images.pexels.com/photos/1287455/pexels-photo-1287455.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CRUISE_IMAGE = "https://images.unsplash.com/photo-1554254648-2d58a1bc3fd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjcnVpc2UlMjBzaGlwJTIwb2NlYW4lMjBzdW5zZXR8ZW58MHx8fHwxNzgzMTE2NjIwfDA&ixlib=rb-4.1.0&q=85";
const RESORT_IMAGE = "https://images.pexels.com/photos/1724430/pexels-photo-1724430.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Services Data
const services = [
  { icon: Palmtree, title: 'Luxury Resorts', desc: 'Handpicked 5-star properties worldwide' },
  { icon: Ship, title: 'Ocean Cruises', desc: 'Unforgettable voyages at sea' },
  { icon: Anchor, title: 'River Cruises', desc: 'Intimate journeys through scenic waterways' },
  { icon: Users, title: 'Group Travel', desc: 'Coordinated trips for any size group' },
  { icon: Heart, title: 'Family Vacations', desc: 'Creating lasting memories together' },
  { icon: Sparkles, title: 'Honeymoons', desc: 'Romantic escapes for newlyweds' },
  { icon: Cake, title: 'Destination Weddings', desc: 'Say "I do" in paradise' },
  { icon: Star, title: 'Anniversary Trips', desc: 'Celebrate your love story' },
  { icon: Calendar, title: 'Birthday Celebrations', desc: 'Milestone trips worth remembering' },
  { icon: Briefcase, title: 'Corporate Travel', desc: 'Business trips done right' },
];

// Why Use a Travel Advisor
const whyAdvisor = [
  { title: 'Saves Time', desc: 'We handle all the research and booking details' },
  { title: 'Personalized Planning', desc: 'Itineraries tailored to your preferences' },
  { title: 'Exclusive Promotions', desc: 'Access to deals not available online' },
  { title: 'Expert Knowledge', desc: 'Firsthand destination experience' },
  { title: 'Cruise Expertise', desc: 'Navigate cabin choices and ship amenities' },
  { title: 'Resort Recommendations', desc: 'Know which properties deliver' },
  { title: 'Visa Guidance', desc: 'Documentation requirements simplified' },
  { title: 'Travel Support', desc: 'Help before, during, and after your trip' },
  { title: 'Change Assistance', desc: 'We handle the unexpected' },
  { title: 'One Point of Contact', desc: 'From planning to return' },
];

const TravelHomePage = () => {
  const [inquiryForm, setInquiryForm] = useState({
    name: '', email: '', phone: '', destination: '', departure_city: '',
    travel_date: '', flexible_dates: false, adults: '2', children: '0',
    children_ages: '', budget: '', special_occasion: '', resort_preference: '',
    cruise_preference: '', additional_details: '', consent: false
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/travel-inquiries`, inquiryForm);
      toast.success('Thank you for your inquiry! Brandi will be in touch within 24 hours.');
      setInquiryForm({
        name: '', email: '', phone: '', destination: '', departure_city: '',
        travel_date: '', flexible_dates: false, adults: '2', children: '0',
        children_ages: '', budget: '', special_occasion: '', resort_preference: '',
        cruise_preference: '', additional_details: '', consent: false
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/90 via-navy-800/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <div className="h-px w-12 bg-gold" />
              <span className="text-gold font-medium tracking-widest text-sm uppercase">Luxury Travel Advisory</span>
            </div>
            
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Luxury Travel, Expert Planning,{' '}
              <span className="text-gold">Unforgettable Experiences</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-4 leading-relaxed">
              I specialize in luxury resorts, cruises, group travel, destination celebrations, and customized vacation planning.
            </p>
            <p className="text-lg text-gold mb-8 font-medium">
              Every traveler deserves exceptional service regardless of budget.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/consultation">
                <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-6 rounded-full text-lg shadow-luxury">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Your Consultation
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy px-8 py-6 rounded-full text-lg">
                  <Mail className="w-5 h-5 mr-2" />
                  Ask a Travel Question
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Consultation Fee Badge */}
        <div className="absolute bottom-8 right-8 hidden lg:block">
          <div className="bg-white rounded-2xl p-6 shadow-luxury max-w-xs">
            <div className="text-center">
              <p className="text-navy font-medium mb-2">Travel Consultation</p>
              <p className="text-4xl font-serif font-bold text-gold mb-2">$25</p>
              <p className="text-sm text-text-secondary">
                Credited toward any vacation booked through our agency
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Fee - Mobile */}
      <section className="lg:hidden bg-navy py-8 px-4">
        <div className="max-w-md mx-auto bg-white rounded-2xl p-6 shadow-luxury text-center">
          <p className="text-navy font-medium mb-2">Travel Consultation</p>
          <p className="text-4xl font-serif font-bold text-gold mb-2">$25</p>
          <p className="text-sm text-text-secondary mb-4">
            Credited toward any vacation booked through our agency
          </p>
          <Link to="/consultation">
            <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-6 py-3 rounded-full">
              Book Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold font-medium tracking-widest text-sm uppercase">What We Offer</span>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mt-4 mb-6">
              Our Travel Services
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto text-lg">
              From romantic getaways to corporate retreats, we plan every detail of your perfect trip.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 text-center hover:shadow-card-hover transition-all duration-300 group cursor-pointer"
              >
                <div className="w-14 h-14 bg-navy/5 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/10 transition-colors">
                  <service.icon className="w-7 h-7 text-navy group-hover:text-gold transition-colors" />
                </div>
                <h3 className="font-semibold text-navy mb-2">{service.title}</h3>
                <p className="text-sm text-text-muted">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Use a Travel Advisor */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-gold font-medium tracking-widest text-sm uppercase">The Advantage</span>
              <h2 className="font-serif text-4xl md:text-5xl text-navy mt-4 mb-6">
                Why Use a Travel Advisor?
              </h2>
              <p className="text-text-secondary text-lg mb-8">
                In a world of endless online options, a dedicated travel advisor brings expertise, 
                personalization, and peace of mind that no website can match.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {whyAdvisor.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-navy">{item.title}</h4>
                      <p className="text-sm text-text-muted">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={CRUISE_IMAGE} 
                alt="Luxury cruise at sunset" 
                className="rounded-2xl shadow-luxury w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold rounded-2xl p-6 shadow-luxury">
                <p className="text-navy font-serif text-3xl font-bold">26+</p>
                <p className="text-navy/80 text-sm">Countries Visited</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Brandi */}
      <section className="py-20 bg-navy relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-gold font-medium tracking-widest text-sm uppercase">Your Travel Expert</span>
              <h2 className="font-serif text-4xl md:text-5xl text-white mt-4 mb-6">
                Meet Brandi Satterfield
              </h2>
              
              <div className="space-y-4 text-white/90 text-lg leading-relaxed">
                <p>
                  Brandi Satterfield has been in the travel industry since 2017 and has traveled 
                  to more than <span className="text-gold font-semibold">26 countries</span> across the world.
                </p>
                <p>
                  Her firsthand travel experience allows her to recommend destinations with confidence 
                  while creating personalized itineraries for every client.
                </p>
                <p>
                  Brandi specializes in <span className="text-gold">luxury resorts, cruises, group travel, 
                  and destination celebrations</span>. She believes every traveler deserves exceptional 
                  service regardless of their budget.
                </p>
                <p>
                  Her attention to detail, organization, and passion for travel help create 
                  stress-free vacations from beginning to end.
                </p>
              </div>
              
              <div className="mt-8 flex items-center gap-4">
                <MapPin className="w-5 h-5 text-gold" />
                <span className="text-white/80">Houston, TX</span>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 flex justify-center relative z-20">
              <div className="relative">
                <div className="w-72 h-72 md:w-96 md:h-96 rounded-full overflow-hidden border-4 border-gold shadow-luxury bg-navy">
                  <img 
                    src={BRANDI_PHOTO} 
                    alt="Brandi Satterfield" 
                    className="w-full h-full object-cover object-center relative z-30"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-gold rounded-full p-4 z-40">
                  <Globe className="w-8 h-8 text-navy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Inquiry Form */}
      <section className="py-20 bg-cream" id="inquiry">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold font-medium tracking-widest text-sm uppercase">Start Planning</span>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mt-4 mb-6">
              Travel Inquiry Form
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto text-lg">
              Tell us about your dream vacation and we'll create a personalized itinerary just for you.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-luxury p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-navy font-medium">Full Name *</Label>
                <Input 
                  required
                  value={inquiryForm.name}
                  onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label className="text-navy font-medium">Email Address *</Label>
                <Input 
                  required
                  type="email"
                  value={inquiryForm.email}
                  onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label className="text-navy font-medium">Phone Number</Label>
                <Input 
                  value={inquiryForm.phone}
                  onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label className="text-navy font-medium">Destination *</Label>
                <Input 
                  required
                  value={inquiryForm.destination}
                  onChange={(e) => setInquiryForm({...inquiryForm, destination: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                  placeholder="e.g., Maldives, Caribbean, Europe"
                />
              </div>
              <div>
                <Label className="text-navy font-medium">Departure City</Label>
                <Input 
                  value={inquiryForm.departure_city}
                  onChange={(e) => setInquiryForm({...inquiryForm, departure_city: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                  placeholder="e.g., Houston, TX"
                />
              </div>
              <div>
                <Label className="text-navy font-medium">Travel Date</Label>
                <Input 
                  type="date"
                  value={inquiryForm.travel_date}
                  onChange={(e) => setInquiryForm({...inquiryForm, travel_date: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                />
              </div>
              <div>
                <Label className="text-navy font-medium">Number of Adults</Label>
                <Input 
                  type="number"
                  min="1"
                  value={inquiryForm.adults}
                  onChange={(e) => setInquiryForm({...inquiryForm, adults: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                />
              </div>
              <div>
                <Label className="text-navy font-medium">Number of Children</Label>
                <Input 
                  type="number"
                  min="0"
                  value={inquiryForm.children}
                  onChange={(e) => setInquiryForm({...inquiryForm, children: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                />
              </div>
              {parseInt(inquiryForm.children) > 0 && (
                <div className="md:col-span-2">
                  <Label className="text-navy font-medium">Ages of Children</Label>
                  <Input 
                    value={inquiryForm.children_ages}
                    onChange={(e) => setInquiryForm({...inquiryForm, children_ages: e.target.value})}
                    className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                    placeholder="e.g., 5, 8, 12"
                  />
                </div>
              )}
              <div>
                <Label className="text-navy font-medium">Budget Range</Label>
                <select
                  value={inquiryForm.budget}
                  onChange={(e) => setInquiryForm({...inquiryForm, budget: e.target.value})}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2 focus:border-gold focus:ring-gold"
                >
                  <option value="">Select budget range</option>
                  <option value="under-5000">Under $5,000</option>
                  <option value="5000-10000">$5,000 - $10,000</option>
                  <option value="10000-20000">$10,000 - $20,000</option>
                  <option value="20000-50000">$20,000 - $50,000</option>
                  <option value="over-50000">$50,000+</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
              <div>
                <Label className="text-navy font-medium">Special Occasion</Label>
                <select
                  value={inquiryForm.special_occasion}
                  onChange={(e) => setInquiryForm({...inquiryForm, special_occasion: e.target.value})}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2 focus:border-gold focus:ring-gold"
                >
                  <option value="">Select if applicable</option>
                  <option value="honeymoon">Honeymoon</option>
                  <option value="anniversary">Anniversary</option>
                  <option value="birthday">Birthday</option>
                  <option value="wedding">Destination Wedding</option>
                  <option value="graduation">Graduation</option>
                  <option value="retirement">Retirement</option>
                  <option value="family-reunion">Family Reunion</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-navy font-medium">Resort or Cruise Preferences</Label>
                <Textarea 
                  value={inquiryForm.cruise_preference}
                  onChange={(e) => setInquiryForm({...inquiryForm, cruise_preference: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                  placeholder="Any specific resorts, cruise lines, or experiences you're interested in?"
                  rows={3}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-navy font-medium">Additional Details</Label>
                <Textarea 
                  value={inquiryForm.additional_details}
                  onChange={(e) => setInquiryForm({...inquiryForm, additional_details: e.target.value})}
                  className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                  placeholder="Tell us more about your ideal vacation..."
                  rows={4}
                />
              </div>
              
              <div className="md:col-span-2 flex items-start gap-3">
                <input 
                  type="checkbox"
                  required
                  checked={inquiryForm.consent}
                  onChange={(e) => setInquiryForm({...inquiryForm, consent: e.target.checked})}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-gold focus:ring-gold"
                />
                <label className="text-sm text-text-secondary">
                  I understand the <span className="font-semibold text-navy">$25 consultation fee</span> is applied 
                  toward any travel booked through Live Life Global Wellness Advisory Group.
                </label>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <Button 
                type="submit"
                disabled={submitting}
                className="bg-gold hover:bg-gold-dark text-navy font-semibold px-12 py-6 rounded-full text-lg shadow-luxury"
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-navy border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Submit Inquiry
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Ready to Start Planning Your Dream Vacation?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Book a consultation and let's create an unforgettable travel experience together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/consultation">
              <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-4 rounded-full">
                <Calendar className="w-5 h-5 mr-2" />
                Book Consultation - $25
              </Button>
            </Link>
            <a href="mailto:brandi@livelifeglobalwellness.com">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy px-8 py-4 rounded-full">
                <Mail className="w-5 h-5 mr-2" />
                brandi@livelifeglobalwellness.com
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TravelHomePage;
